import React, { createContext, useContext, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import InstituteSwitcher from "@/components/InstituteSwitcher";
import AboutMenu from "@/components/AboutMenu";
import AdmissionInquiryModal from "@/pages/public/polyrotegaon/AdmissionInquiryModal";
import {
  Menu, X, Phone, Mail, MapPin, ArrowUpRight,
  Facebook, Instagram, Youtube, Linkedin,
} from "lucide-react";

const MitmcvcContext = createContext(null);
export const useMitmcvc = () => useContext(MitmcvcContext);

export const NAV = [
  { to: "", end: true, label: "मुख्यपृष्ठ" },
  { to: "about", label: "आमच्याविषयी", isAboutMenu: true },
  { to: "gallery", label: "छायाचित्रे" },
  { to: "contact", label: "संपर्क" },
];

const ABOUT_ITEMS = [
  { to: "about", label: "आमच्याविषयी" },
  { to: "leadership", label: "नेतृत्वाचा संदेश" },
  { to: "executive-body", label: "कार्यकारी मंडळ" },
];

export default function MitmcvcLayout() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/website/mitmcvc");
        setData(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "वेबसाइट उपलब्ध नाही");
      } finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setMobileOpen(false);
  }, [location.pathname]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-white"><div className="text-slate-500 font-serif text-xl">लोड होत आहे…</div></div>;
  }
  if (err || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="text-center">
          <h1 className="font-serif text-3xl text-slate-800">वेबसाइट सापडली नाही</h1>
          <p className="mt-2 text-slate-500">{err}</p>
        </div>
      </div>
    );
  }

  const inst = data.institute;
  const logoSrc = inst.logo_url || "/assets/mitcsn-logo.png";

  const linkClass = ({ isActive }) =>
    `text-[13px] px-4 py-2.5 font-semibold uppercase tracking-wider whitespace-nowrap transition-all ${
      isActive
        ? "bg-[#F59E0B] text-[#14532D]"
        : "text-white/85 hover:bg-white/10 hover:text-white"
    }`;

  return (
    <MitmcvcContext.Provider value={data}>
      <Helmet prioritizeSeoTags>
        <title>एम.आय.टी. एच.एस.सी. व्होकेशनल (MCVC) कनिष्ठ महाविद्यालय · छत्रपती संभाजीनगर</title>
        <meta name="description" content="ग्रामोद्योगिक शिक्षण मंडळ, छत्रपती संभाजीनगर संचलित एम.आय.टी. एच.एस.सी. व्होकेशनल (एम.सी.व्ही.सी.) कनिष्ठ महाविद्यालय — सिडको एन-४. महाराष्ट्र राज्य माध्यमिक व उच्च माध्यमिक शिक्षण मंडळ, पुणे यांची संलग्नता." />
        <link rel="canonical" href="https://gsmandal.org/mitmcvc" />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-[#FEF8E8]">
        {/* Top utility bar */}
        <div className="bg-[#052E16] text-amber-100/80 text-[11px]">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-4 flex-wrap">
              {inst.admission_phone && (
                <a href={`tel:${inst.admission_phone.replace(/\s+/g, "")}`} data-testid="mitmcvc-top-phone" className="inline-flex items-center gap-1 hover:text-[#F59E0B]"><Phone size={11} className="text-[#F59E0B]" />{inst.admission_phone}</a>
              )}
              {inst.email && (
                <a href={`mailto:${inst.email}`} data-testid="mitmcvc-top-email" className="hidden md:inline-flex items-center gap-1 hover:text-[#F59E0B]"><Mail size={11} className="text-[#F59E0B]" />{inst.email}</a>
              )}
              <span className="hidden lg:inline-flex items-center gap-1"><MapPin size={11} className="text-[#F59E0B]" />सिडको एन-४, छत्रपती संभाजीनगर</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <InstituteSwitcher currentSlug="mitmcvc" testidPrefix="mitmcvc-top-switcher" />
              {inst.facebook_url && (<a href={inst.facebook_url} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-[#F59E0B]"><Facebook size={13} /></a>)}
              {inst.instagram_url && (<a href={inst.instagram_url} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-[#F59E0B]"><Instagram size={13} /></a>)}
              {inst.linkedin_url && (<a href={inst.linkedin_url} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="hover:text-[#F59E0B]"><Linkedin size={13} /></a>)}
              {inst.youtube_url && (<a href={inst.youtube_url} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="hover:text-[#F59E0B]"><Youtube size={13} /></a>)}
            </div>
          </div>
        </div>

        {/* Logo bar */}
        <div className="bg-white border-b-4 border-[#F59E0B]">
          <div className="max-w-7xl mx-auto px-4 py-4 grid grid-cols-[auto_1fr] md:grid-cols-[auto_1fr_auto] items-center gap-4">
            <NavLink to="" end className="flex items-center justify-start shrink-0" data-testid="mitmcvc-logo-link">
              <img src={logoSrc} alt={inst.name} className="h-16 md:h-20 w-auto object-contain" />
            </NavLink>
            <div className="text-center leading-tight">
              <div className="text-[10px] md:text-xs tracking-[0.2em] uppercase text-[#B45309] font-bold">ग्रामोद्योगिक शिक्षण मंडळ, छत्रपती संभाजीनगर</div>
              <div className="font-serif font-black text-lg md:text-2xl text-[#14532D] mt-1 leading-tight" data-testid="mitmcvc-header-name">
                एम.आय.टी. एच.एस.सी. व्होकेशनल (एम.सी.व्ही.सी.) कनिष्ठ महाविद्यालय
              </div>
              <div className="font-serif text-sm md:text-base text-[#166534] mt-0.5 italic">सिडको एन-४ · छत्रपती संभाजीनगर · स्थापना १९८८–८९</div>
              <div className="text-[10px] md:text-[11px] tracking-[0.12em] uppercase text-slate-600 mt-1">
                संस्था नोंदणी क्रमांक · 56.01.066 &nbsp;·&nbsp; U-DISE · 90100000095
              </div>
            </div>
            <div className="hidden md:flex flex-col items-end gap-1">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#14532D] font-bold">HSC बोर्ड संलग्न</div>
              <div className="text-[10px] tracking-[0.15em] uppercase text-slate-500">महा. राज्य मंडळ · पुणे</div>
              <button type="button" onClick={() => setInquiryOpen(true)} className="mt-1 inline-flex items-center gap-1.5 bg-[#14532D] hover:bg-[#052E16] text-white text-[10px] uppercase tracking-wider font-bold px-3 py-1.5">प्रवेश चौकशी <ArrowUpRight size={10} strokeWidth={2.5} /></button>
            </div>
          </div>
        </div>

        {/* Main nav */}
        <nav className="sticky top-0 z-40 bg-[#14532D] shadow-md" data-testid="mitmcvc-nav">
          <div className="max-w-7xl mx-auto px-2">
            <div className="hidden lg:flex items-center gap-0 py-0 flex-wrap">
              {NAV.map((n) => (
                n.isAboutMenu ? (
                  <AboutMenu
                    key="about"
                    linkClass={linkClass}
                    testidPrefix="mitmcvc-nav-about"
                    menuClassName="bg-[#14532D] text-white ring-1 ring-white/10"
                    itemHoverClassName="hover:bg-white/10"
                    items={ABOUT_ITEMS}
                    triggerLabel="आमच्याविषयी"
                  />
                ) : (
                  <NavLink key={n.to} to={n.to} end={n.end} data-testid={`mitmcvc-nav-${n.to || "home"}`} className={linkClass}>
                    {n.label}
                  </NavLink>
                )
              ))}
            </div>
            <div className="lg:hidden flex items-center justify-between py-2 px-2">
              <span className="text-white text-[11px] tracking-[0.25em] uppercase font-bold">Menu</span>
              <button onClick={() => setMobileOpen((v) => !v)} className="p-2 text-white" aria-label="Menu" data-testid="mitmcvc-nav-toggle">{mobileOpen ? <X /> : <Menu />}</button>
            </div>
            {mobileOpen && (
              <div className="lg:hidden pb-3 flex flex-col">
                {NAV.map((n) => (
                  n.isAboutMenu ? (
                    <React.Fragment key="about-mob">
                      {ABOUT_ITEMS.map((it) => (
                        <NavLink
                          key={it.to}
                          to={it.to}
                          end={it.to === "about"}
                          onClick={() => setMobileOpen(false)}
                          className={linkClass}
                        >
                          {it.label}
                        </NavLink>
                      ))}
                    </React.Fragment>
                  ) : (
                    <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMobileOpen(false)} className={linkClass}>{n.label}</NavLink>
                  )
                ))}
              </div>
            )}
          </div>
        </nav>

        <main className="flex-1 bg-[#FEF8E8]"><Outlet /></main>

        {/* Footer */}
        <footer className="bg-[#052E16] text-amber-100/80">
          <div className="max-w-7xl mx-auto px-5 py-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-3">
                <img src={logoSrc} alt={inst.name} className="h-14 w-auto object-contain bg-white p-1.5 rounded" />
                <div>
                  <div className="text-white font-serif text-base leading-tight">एम.आय.टी. एच.एस.सी. व्होकेशनल</div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#F59E0B] font-bold mt-1">जी.एस. मंडळ संस्था</div>
                </div>
              </div>
              <p className="mt-5 text-sm leading-relaxed">
                १९८८–८९ पासून छत्रपती संभाजीनगरातील सिडको एन-४ येथे कार्यरत — ३००० पेक्षा जास्त विद्यार्थ्यांनी मोठ्या कंपन्यांमध्ये व शासकीय आस्थापनांत नोकरी मिळवली किंवा स्वतःचा व्यवसाय सुरू केला.
              </p>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#F59E0B] font-bold mb-3">जलद दुवे</div>
              <ul className="space-y-1.5 text-sm">
                {NAV.map((n) => <li key={n.to}><NavLink to={n.to} end={n.end} className="hover:text-white">{n.label}</NavLink></li>)}
                {ABOUT_ITEMS.slice(1).map((it) => <li key={it.to}><Link to={it.to} className="hover:text-white">{it.label}</Link></li>)}
              </ul>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#F59E0B] font-bold mb-3">संपर्क</div>
              <div className="text-sm space-y-2 leading-relaxed">
                <div className="flex items-start gap-2"><MapPin size={13} className="text-[#F59E0B] mt-0.5 shrink-0" /><span>एमआयटी कॅम्पस, सिडको एन-४, छत्रपती संभाजीनगर, महाराष्ट्र</span></div>
                {inst.admission_phone && <div className="flex items-center gap-2"><Phone size={13} className="text-[#F59E0B]" /><a href={`tel:${inst.admission_phone.replace(/\s+/g, "")}`} className="hover:text-white">{inst.admission_phone}</a></div>}
                {inst.email && <div className="flex items-center gap-2"><Mail size={13} className="text-[#F59E0B]" /><a href={`mailto:${inst.email}`} className="hover:text-white break-all">{inst.email}</a></div>}
              </div>
            </div>
          </div>
          <div className="border-t border-amber-100/10">
            <div className="max-w-7xl mx-auto px-5 py-4 flex items-center justify-between flex-wrap gap-2 text-[11px] text-amber-100/60">
              <div>© {new Date().getFullYear()} एम.आय.टी. एच.एस.सी. व्होकेशनल कनिष्ठ महाविद्यालय · ग्रामोद्योगिक शिक्षण मंडळ.</div>
              <div className="flex items-center gap-1">Website by <span className="text-[#F59E0B] font-semibold">GS Mandal CMS</span></div>
            </div>
          </div>
        </footer>
      </div>

      <AdmissionInquiryModal open={inquiryOpen} onClose={() => setInquiryOpen(false)} slug="mitmcvc" departments={data?.departments || []} />
    </MitmcvcContext.Provider>
  );
}
